import { useState } from 'react'
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  bulkMarkRead,
  clearDnd,
  getPreferences,
  getUnreadCount,
  listNotifications,
  markAllRead,
  markRead,
  setDnd as setDndApi,
  updatePreferences,
} from '@/features/notifications/api'
import { useWsSubscription } from '@/hooks/useWebSocket'
import type {
  BulkMarkReadPayload,
  Notification,
  NotificationPreferences,
  NotificationsListFilters,
  NotificationsListResponse,
  PreferencesResponse,
  SetDndPayload,
  UnreadCountResponse,
} from '@/features/notifications/types'

const LIST_LIMIT = 10

export const notificationsKeys = {
  list: (f: Omit<NotificationsListFilters, 'cursor'>) =>
    ['notifications', 'list', f] as const,
  unread: ['notifications', 'unread'] as const,
  preferences: ['notifications', 'preferences'] as const,
}

/**
 * AF-134 — paginated notifications query. Used by the bell preview
 * (limit=10, no filters) and the center page (filters + infinite
 * scroll). React Query infinite-query keeps the cursor model simple.
 */
export function useNotifications(
  filters: Omit<NotificationsListFilters, 'cursor'> = {},
) {
  return useInfiniteQuery<NotificationsListResponse>({
    queryKey: notificationsKeys.list(filters),
    queryFn: ({ pageParam }) =>
      listNotifications({
        ...filters,
        cursor: typeof pageParam === 'string' ? pageParam : undefined,
        limit: filters.limit ?? LIST_LIMIT,
      }),
    initialPageParam: undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    staleTime: 15_000,
  })
}

export function useUnreadCount() {
  return useQuery<UnreadCountResponse>({
    queryKey: notificationsKeys.unread,
    queryFn: () => getUnreadCount(),
    staleTime: 5_000,
  })
}

export function useMarkRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => markRead(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] })
    },
  })
}

export function useMarkAllRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => markAllRead(),
    onSuccess: ({ marked }) => {
      void qc.invalidateQueries({ queryKey: ['notifications'] })
      toast.success(
        marked === 0 ? 'No unread notifications' : `Marked ${marked} as read`,
      )
    },
  })
}

export function useBulkMarkRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (p: BulkMarkReadPayload) => bulkMarkRead(p),
    onSuccess: ({ marked }) => {
      void qc.invalidateQueries({ queryKey: ['notifications'] })
      toast.success(
        marked === 0 ? 'Already read' : `Marked ${marked} as read`,
      )
    },
  })
}

export function usePreferences() {
  return useQuery<PreferencesResponse>({
    queryKey: notificationsKeys.preferences,
    queryFn: () => getPreferences(),
    staleTime: 60_000,
  })
}

export function useUpdatePreferences() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (prefs: NotificationPreferences) => updatePreferences(prefs),
    onSuccess: (next) => {
      qc.setQueryData(notificationsKeys.preferences, next)
      toast.success('Preferences saved')
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : 'Save failed'),
  })
}

export function useSetDnd() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (p: SetDndPayload) => setDndApi(p),
    onSuccess: (next) => {
      qc.setQueryData(notificationsKeys.preferences, next)
      toast.success('Do-not-disturb on')
    },
  })
}

export function useClearDnd() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => clearDnd(),
    onSuccess: (next) => {
      qc.setQueryData(notificationsKeys.preferences, next)
      toast.success('Do-not-disturb off')
    },
  })
}

interface NotificationCreatedPayload {
  notification: Notification
}

/**
 * AF-134 — global subscription to `notifications.*` WebSocket events.
 * Mounted once at the app root via `<NotificationsLive />`.
 *
 * Behaviour:
 *   - On `notifications.created`: invalidate the list + unread count so
 *     the badge increments and the dropdown refreshes.
 *   - Critical-level events also fire a sonner toast (per technical
 *     note: "critical-only toasts, everything else just increments the
 *     badge").
 *   - DND suppresses non-critical toasts AND aria-live announcements;
 *     critical events always break DND.
 *
 * Returns the latest aria-live text so a wrapper component can render
 * it inside `<div role="status" aria-live="polite">…</div>`.
 */
export function useNotificationsLive(): { liveText: string } {
  const qc = useQueryClient()
  const prefs = usePreferences()
  const [liveText, setLiveText] = useState('')

  useWsSubscription(/^notifications\./, (event) => {
    void qc.invalidateQueries({ queryKey: ['notifications'] })
    if (event.type !== 'notifications.created') return

    const payload = event.payload as NotificationCreatedPayload | null
    const n = payload?.notification
    if (!n) return

    const inDnd = prefs.data?.dnd.enabled ?? false
    const isCritical = n.level === 'critical'
    if (inDnd && !isCritical) return

    if (isCritical) {
      toast.error(n.title, { description: n.message })
    }
    setLiveText(`${n.title}. ${n.message}`)
  })

  return { liveText }
}
